import React, { createContext, useContext, useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "../config/authConfig";
import { logError } from "../utils/logger";

const AuthContext = createContext(null);
const API_ROOT = process.env.REACT_APP_API_ROOT;

export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const user =
    isAuthenticated && accounts.length > 0
      ? { username: accounts[0].username, name: accounts[0].name }
      : null;

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authzChecked, setAuthzChecked] = useState(false);
  const [authzError, setAuthzError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.username) {
      setIsAuthorized(false);
      setAuthzChecked(false);
      setAuthzError(null);
      return;
    }

    let cancelled = false;
    setAuthzChecked(false);
    setAuthzError(null);

    fetch(`${API_ROOT}/adminuser/check?email=${encodeURIComponent(user.username)}`)
      .then((res) => {
        if (!res.ok) throw new Error("Authorization check failed");
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setIsAuthorized(!!data.authorized);
        setAuthzChecked(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setAuthzError(err.message || "Authorization check failed");
        setIsAuthorized(false);
        setAuthzChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.username]);

  const login = () => {
    instance.loginRedirect(loginRequest).catch((error) => {
      logError("Login redirect failed:", error);
    });
  };

  const logout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: `${window.location.origin}/login` });
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, isAuthorized, authzChecked, authzError, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
